import { useState, useRef, useEffect } from 'react';
import { Upload, X, Edit2 } from 'lucide-react';
import { RecortadorImagen } from '../RecortadorImagen';

export interface SubirImagenProps {
  value?: string | null;
  onChange?: (croppedImage: string | null) => void;
  maxSizeMB?: number;
  label?: string;
  hint?: string;
  aspectRatio?: number;
  boxSize?: number;
  className?: string;
}

const SubirImagen: React.FC<SubirImagenProps> = ({
  value = null,
  onChange,
  maxSizeMB = 5,
  label = 'Foto del cliente',
  hint = 'Máx. 5MB',
  aspectRatio = 1,
  boxSize = 112,
  className = '',
}) => {
  const [image, setImage] = useState<string | null>(value);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sincronizar el estado interno con el value prop (útil para edición de clientes existentes)
  useEffect(() => {
    setImage(value);
  }, [value]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      // Validar tamaño
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSizeMB) {
        alert(`La imagen excede el tamaño máximo de ${maxSizeMB}MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageSrc = e.target?.result as string;
        setOriginalImage(imageSrc);
        setIsEditing(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemove = () => {
    setImage(null);
    setOriginalImage(null);
    setIsEditing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange?.(null);
  };

  const handleEdit = () => {
    // Si hay imagen pero no originalImage (caso de edición de cliente existente),
    // usar la imagen actual como originalImage
    if (image && !originalImage) {
      setOriginalImage(image);
      setIsEditing(true);
    } else if (originalImage) {
      setIsEditing(true);
    }
  };

  const handleCancelCrop = () => {
    setIsEditing(false);
    // Si no hay imagen previa, limpiar todo
    if (!image) {
      setOriginalImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileSelect = () => {
    if (!image) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-ui-sm font-medium mb-1.5 text-tertiary">
          {label}
        </label>
      )}

      <div
        className={`
          relative rounded-lg overflow-hidden cursor-pointer transition-all duration-200
          ${isDragging 
            ? 'border-2 border-solid border-brand bg-info-bg' 
            : image 
              ? 'border-2 border-solid border-border-default bg-surface-1'
              : 'border-2 border-dashed border-border-default bg-surface-1 hover:border-brand hover:bg-info-bg'
          }
        `}
        style={{ width: `${boxSize}px`, height: `${boxSize}px` }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={triggerFileSelect}
        role="button"
        tabIndex={0}
        aria-label="Subir imagen"
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            triggerFileSelect();
          }
        }}
      >
        {!image ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-3 text-center">
            <Upload className={`w-8 h-8 mb-1.5 transition-colors ${isDragging ? 'text-brand' : 'text-tertiary'}`} />
            <p className="text-[11px] font-medium text-secondary mb-0.5 leading-tight">
              <button
                type="button"
                className="text-brand font-medium cursor-pointer hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                Subir
              </button>
            </p>
            <p className="text-[10px] text-tertiary leading-tight">JPG, PNG</p>
          </div>
        ) : (
          <>
              <div className="relative w-full h-full flex items-center justify-center bg-surface-1">
              <img 
                src={image} 
                alt="Preview" 
                className="max-w-full max-h-full object-cover rounded"
              />
            </div>
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 opacity-0 hover:opacity-100 flex items-center justify-center gap-1.5 transition-all duration-200">
              <button
                type="button"
                className="w-8 h-8 rounded-md bg-white flex items-center justify-center shadow-lg hover:bg-gray-100 hover:scale-105 transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit();
                }}
                aria-label="Editar imagen"
              >
                <Edit2 size={14} className="text-gray-700" />
              </button>
              <button
                type="button"
                className="w-8 h-8 rounded-md bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg hover:scale-105 transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                aria-label="Eliminar imagen"
              >
                <X size={14} className="text-white" />
              </button>
            </div>
          </>
        )}
      </div>

      {hint && (
        <p className="text-[11px] text-tertiary mt-1.5 whitespace-pre-line">{hint}</p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        className="hidden"
      />

      {isEditing && originalImage && (
        <RecortadorImagen
          src={originalImage}
          aspectRatio={aspectRatio}
          onSave={(cropped) => {
            setImage(cropped);
            setIsEditing(false);
            onChange?.(cropped);
          }}
          onCancel={handleCancelCrop}
        />
      )}
    </div>
  );
};

SubirImagen.displayName = 'SubirImagen';

export default SubirImagen;


